import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiGet, apiPost, apiPut } from '../services/api';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';

interface Vendor { id: string; name: string; }
interface Category { id: string; name: string; }

interface ExpenseData {
  vendor_id: string;
  category_id: string;
  expense_date: string;
  due_date: string;
  description: string;
  subtotal: string;
  tax_amount: string;
  total: string;
  currency: string;
  status: string;
  notes: string;
}

export default function ExpenseForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [formData, setFormData] = useState<ExpenseData>({
    vendor_id: '',
    category_id: '',
    expense_date: new Date().toISOString().split('T')[0],
    due_date: '',
    description: '',
    subtotal: '0',
    tax_amount: '0',
    total: '0',
    currency: 'ZAR',
    status: 'pending',
    notes: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchVendors();
    fetchCategories();
    if (isEditing) fetchExpense();
  }, [id]);

  const fetchVendors = async () => {
    try {
      const data = await apiGet<{ items: Vendor[] }>('/api/vendors?page_size=100');
      setVendors(data.items);
    } catch (err) { console.error(err); }
  };

  const fetchCategories = async () => {
    try {
      const data = await apiGet<{ items: Category[] }>('/api/categories?category_type=expense&page_size=100');
      setCategories(data.items);
    } catch (err) { console.error(err); }
  };

  const fetchExpense = async () => {
    setIsLoading(true);
    try {
      const data = await apiGet<any>(`/api/expenses/${id}`);
      setFormData({
        vendor_id: data.vendor_id || '',
        category_id: data.category_id || '',
        expense_date: data.expense_date?.split('T')[0] || '',
        due_date: data.due_date?.split('T')[0] || '',
        description: data.description || '',
        subtotal: data.subtotal?.toString() || '0',
        tax_amount: data.tax_amount?.toString() || '0',
        total: data.total?.toString() || '0',
        currency: data.currency || 'ZAR',
        status: data.status || 'pending',
        notes: data.notes || '',
      });
    } catch (err) {
      setError('Failed to load expense');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);

    try {
      const payload = {
        vendor_id: formData.vendor_id || null,
        category_id: formData.category_id || null,
        expense_date: formData.expense_date,
        due_date: formData.due_date || null,
        description: formData.description,
        subtotal: formData.subtotal,
        tax_amount: formData.tax_amount,
        total: formData.total,
        currency: formData.currency,
        status: formData.status,
        notes: formData.notes || null,
      };

      if (isEditing) {
        await apiPut(`/api/expenses/${id}`, payload);
      } else {
        await apiPost('/api/expenses', payload);
      }
      navigate('/admin/expenses');
    } catch (err: any) {
      setError(err.message || 'Failed to save expense');
    } finally {
      setIsSaving(false);
    }
  };

  const calculateTotal = (subtotal: string, tax: string) => {
    return (Number(subtotal) + Number(tax)).toFixed(2);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'subtotal' || name === 'tax_amount') {
        updated.total = calculateTotal(
          name === 'subtotal' ? value : prev.subtotal,
          name === 'tax_amount' ? value : prev.tax_amount
        );
      }
      return updated;
    });
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-primary-500 animate-spin" /></div>;
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <button onClick={() => navigate('/admin/expenses')} className="flex items-center gap-2 text-gray-400 hover:text-white">
          <ArrowLeft className="w-4 h-4" /> Back to Expenses
        </button>
      </div>

      <div className="bg-dark-900 rounded-xl border border-dark-800 p-6">
        <h1 className="text-2xl font-bold text-white mb-6">{isEditing ? 'Edit Expense' : 'New Expense'}</h1>

        {error && <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Vendor</label>
              <select name="vendor_id" value={formData.vendor_id} onChange={handleChange} className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option value="">Select vendor</option>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Category</label>
              <select name="category_id" value={formData.category_id} onChange={handleChange} className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option value="">Select category</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Expense Date *</label>
              <input type="date" name="expense_date" value={formData.expense_date} onChange={handleChange} required className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Due Date</label>
              <input type="date" name="due_date" value={formData.due_date} onChange={handleChange} className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-1">Description *</label>
              <input type="text" name="description" value={formData.description} onChange={handleChange} required className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Subtotal</label>
              <input type="number" name="subtotal" value={formData.subtotal} onChange={handleChange} step="0.01" className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Tax Amount</label>
              <input type="number" name="tax_amount" value={formData.tax_amount} onChange={handleChange} step="0.01" className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Total</label>
              <input type="number" name="total" value={formData.total} readOnly className="w-full px-4 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Status</label>
              <select name="status" value={formData.status} onChange={handleChange} className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="paid">Paid</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-1">Notes</label>
              <textarea name="notes" value={formData.notes} onChange={handleChange} rows={3} className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <button type="submit" disabled={isSaving} className="flex items-center gap-2 px-6 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white rounded-lg">
              {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              {isSaving ? 'Saving...' : 'Save Expense'}
            </button>
            <button type="button" onClick={() => navigate('/admin/expenses')} className="px-6 py-2 bg-dark-700 hover:bg-dark-600 text-white rounded-lg">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}
